import { useState, useEffect } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Building2,
  Hash,
  Loader2,
  Mail,
  Lock,
  User,
  MapPin,
  Phone,
  CreditCard,
  ArrowLeft,
  Check,
  Eye,
  EyeOff,
  FileDown,
  FileText,
  Sparkles,
} from 'lucide-react'
import { authApi, legalDocumentsApi, RegisterWithPlanRequest, RegisterWithPlanResponse, RegisterWithTrialResponse, LegalDocumentInfo } from '../services/api'
import { formatDateDDMMYYYY } from '../utils/date'

export default function RegisterPage() {
  const [searchParams] = useSearchParams()
  const { isAuthenticated, isLoading: authLoading } = useAuth0()
  const navigate = useNavigate()

  const planId = searchParams.get('plan') || 'extended'
  const period = (searchParams.get('period') as 'monthly' | 'yearly') || 'yearly'
  const isTrial = searchParams.get('trial') === 'true'

  // Already authenticated → redirect to /pricing which will create the order (or activate trial)
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      const trialParam = isTrial ? '&trial=true' : ''
      navigate(`/pricing?plan=${planId}&period=${period}${trialParam}`, { replace: true })
    }
  }, [authLoading, isAuthenticated, planId, period, isTrial, navigate])

  const [formData, setFormData] = useState<RegisterWithPlanRequest>({
    email: '',
    password: '',
    organizationName: '',
    userName: '',
    nip: '',
    address: '',
    phoneNumber: '',
    planId,
    billingPeriod: period,
  })
  const [nipError, setNipError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [orderData, setOrderData] = useState<RegisterWithPlanResponse | null>(null)
  const [trialData, setTrialData] = useState<RegisterWithTrialResponse | null>(null)

  // Consent checkboxes
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [acceptPrivacy, setAcceptPrivacy] = useState(false)
  const [acceptB2B, setAcceptB2B] = useState(false)
  const [acceptDPA, setAcceptDPA] = useState(false)
  const [marketingEmail, setMarketingEmail] = useState(false)
  const [marketingPhone, setMarketingPhone] = useState(false)
  const [discountCode, setDiscountCode] = useState('')

  // Fetch legal document versions
  const { data: legalDocs } = useQuery({
    queryKey: ['legal-documents'],
    queryFn: () => legalDocumentsApi.getCurrent().then(r => r.data),
  })

  const getLegalDoc = (slug: string): LegalDocumentInfo | undefined =>
    legalDocs?.find(d => d.slug === slug)

  const formatLegalDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`
  }

  // Register with payment
  const registerMutation = useMutation({
    mutationFn: (data: RegisterWithPlanRequest) => authApi.registerWithPlan(data),
    onSuccess: (response) => {
      setOrderData(response.data)
    },
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      const errorMessage =
        err.response?.data?.error ||
        'Wystąpił błąd podczas rejestracji. Spróbuj ponownie.'
      setError(errorMessage)
    },
  })

  // Register with trial
  const registerTrialMutation = useMutation({
    mutationFn: (data: RegisterWithPlanRequest) => authApi.registerWithTrial(data),
    onSuccess: (response) => {
      setTrialData(response.data)
    },
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      const errorMessage =
        err.response?.data?.error ||
        'Wystąpił błąd podczas rejestracji. Spróbuj ponownie.'
      setError(errorMessage)
    },
  })

  /** Validate Polish NIP (10 digits with checksum) */
  const validateNip = (nip: string): string | null => {
    const digits = nip.replace(/[\s-]/g, '')
    if (!digits) return 'NIP jest wymagany'
    if (!/^\d{10}$/.test(digits)) return 'NIP musi składać się z 10 cyfr'
    const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7]
    let sum = 0
    for (let i = 0; i < 9; i++) {
      sum += parseInt(digits[i]) * weights[i]
    }
    if (sum % 11 !== parseInt(digits[9])) {
      return 'Nieprawidłowy NIP - błędna suma kontrolna'
    }
    return null
  }

  const handleNipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setFormData(prev => ({ ...prev, nip: value }))
    if (nipError) setNipError(null)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const validateForm = (): boolean => {
    setError(null)
    setNipError(null)

    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError('Podaj prawidłowy adres email')
      return false
    }
    if (!formData.password || formData.password.length < 8) {
      setError('Hasło musi mieć co najmniej 8 znaków')
      return false
    }
    if (!formData.organizationName.trim()) {
      setError('Nazwa organizacji jest wymagana')
      return false
    }
    const nipValidationError = validateNip(formData.nip)
    if (nipValidationError) {
      setNipError(nipValidationError)
      return false
    }
    const phone = formData.phoneNumber.replace(/[\s-]/g, '')
    if (!phone) {
      setError('Numer telefonu jest wymagany')
      return false
    }
    if (!/^\+?\d{7,15}$/.test(phone)) {
      setError('Podaj prawidłowy numer telefonu (7-15 cyfr, opcjonalnie z prefixem +)')
      return false
    }
    if (!acceptTerms || !acceptPrivacy || !acceptB2B || !acceptDPA) {
      setError('Zaznacz wszystkie wymagane zgody')
      return false
    }
    return true
  }

  const handleSubmitPayment = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    const normalizedData = {
      ...formData,
      nip: formData.nip.replace(/[\s-]/g, ''),
      discountCode: discountCode.trim() || undefined,
      acceptTerms,
      acceptPrivacy,
      acceptB2B,
      acceptDPA,
      marketingEmail,
      marketingPhone,
    }

    if (isTrial) {
      registerTrialMutation.mutate(normalizedData)
    } else {
      registerMutation.mutate(normalizedData)
    }
  }

  const isLoading = registerMutation.isPending || registerTrialMutation.isPending

  const PLAN_LABELS: Record<string, string> = {
    basic: 'Pakiet Solo',
    extended: 'Pakiet Kancelaria',
    advanced: 'Pakiet Enterprise',
  }


  // Trial success screen
  if (trialData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
        <div className="max-w-lg w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <img src="/assets/logo.png" alt="LexTool" className="h-12 mx-auto mb-4 object-contain" />
              <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900">
                Konto utworzone — okres próbny aktywny!
              </h1>
              <p className="text-gray-500 mt-2">
                Możesz teraz zalogować się i korzystać z LexTool przez 7 dni.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-5 mb-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Plan:</span>
                <span className="font-medium text-gray-900">
                  {PLAN_LABELS[trialData.planName || ''] || PLAN_LABELS[planId] || trialData.planName || planId}
                </span>
              </div>
              {trialData.trialEndsAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Dostęp do:</span>
                  <span className="font-medium text-gray-900">
                    {formatDateDDMMYYYY(trialData.trialEndsAt)}
                  </span>
                </div>
              )}
            </div>

            <Link
              to="/login"
              className="w-full btn-primary py-3 text-base flex items-center justify-center gap-2"
            >
              <Sparkles className="w-5 h-5" />
              Zaloguj się
            </Link>

            <p className="text-xs text-gray-400 text-center mt-4">
              Okres próbny nie wymaga karty płatniczej.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Confirmation step: show proforma + pay button
  if (orderData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
        <div className="max-w-lg w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <img src="/assets/logo.png" alt="LexTool" className="h-12 mx-auto mb-4 object-contain" />
              <h1 className="text-2xl font-bold text-gray-900">
                Konto utworzone
              </h1>
              <p className="text-gray-500 mt-2">Sprawdź proformę i przejdź do płatności</p>
            </div>

            {/* Order summary */}
            <div className="bg-gray-50 rounded-lg p-5 mb-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Plan:</span>
                <span className="font-medium text-gray-900">
                  {PLAN_LABELS[planId] || planId}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Okres rozliczeniowy:</span>
                <span className="font-medium text-gray-900">
                  {period === 'yearly' ? 'Roczny' : 'Miesięczny'}
                </span>
              </div>
              <div className="border-t border-gray-200 pt-3 flex justify-between">
                <span className="text-gray-700 font-medium">Do zapłaty:</span>
                <span className="text-xl font-bold text-gray-900">
                  {orderData.amount.toFixed(2)} {orderData.currency}
                  <span className="text-xs font-normal text-gray-500 ml-1">brutto</span>
                </span>
              </div>
            </div>

            {/* Proforma link */}
            {orderData.proformaUrl && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      Faktura proforma
                      {orderData.proformaNumber && (
                        <span className="font-normal text-blue-700"> nr {orderData.proformaNumber}</span>
                      )}
                    </p>
                    <a
                      href={orderData.proformaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      Pobierz / podgląd proformy
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Pay button */}
            <button
              onClick={() => { window.location.href = orderData.paymentUrl }}
              className="w-full btn-primary py-3 text-base flex items-center justify-center gap-2"
            >
              <CreditCard className="w-5 h-5" />
              Przejdź do płatności
            </button>

            <p className="text-xs text-gray-400 text-center mt-4">
              Zostaniesz przekierowany do bezpiecznej bramki płatniczej Przelewy24.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back to pricing */}
        <a
          href="https://www.lextool.pl/cennik"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Wróć do cennika
        </a>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <img src="/assets/logo.png" alt="LexTool" className="h-14 mx-auto mb-4 object-contain" />
            <h1 className="text-2xl font-bold text-gray-900">
              Utwórz konto w LexTool
            </h1>
            <p className="text-gray-500 mt-2">
              Wypełnij dane, aby rozpocząć korzystanie z LexTool
            </p>
          </div>

          {/* Billing period display (read-only, set via URL) - hide for trial */}
          {!isTrial && (
            <div className="flex items-center gap-3 mb-6">
              <span className="text-sm font-medium text-gray-700">Okres rozliczeniowy:</span>
              <span className="px-3 py-1 rounded text-xs font-medium bg-blue-600 text-white">
                {formData.billingPeriod === 'yearly' ? 'Rocznie (-17%)' : 'Miesięcznie'}
              </span>
            </div>
          )}

          {/* Trial banner */}
          {isTrial && (
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-700">7-dniowy darmowy okres próbny</span>
            </div>
          )}

          {/* B2B notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
            <p className="text-sm text-blue-800">
              LexTool jest narzędziem dla firm i kancelarii prawnych. Do rejestracji wymagany jest NIP.
            </p>
          </div>

          {/* Registration form */}
          <form onSubmit={handleSubmitPayment} className="space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Adres email *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="jan@kancelaria.pl"
                  className="input pl-10 w-full"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Hasło *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Minimum 8 znaków"
                  className="input pl-10 pr-10 w-full"
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Minimum 8 znaków</p>
            </div>

            {/* Organization name */}
            <div>
              <label htmlFor="organizationName" className="block text-sm font-medium text-gray-700 mb-1">
                Nazwa kancelarii / firmy *
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  id="organizationName"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={handleChange}
                  placeholder="np. Kancelaria Kowalski & Partnerzy"
                  className="input pl-10 w-full"
                  required
                />
              </div>
            </div>

            {/* User name */}
            <div>
              <label htmlFor="userName" className="block text-sm font-medium text-gray-700 mb-1">
                Imię i nazwisko
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  id="userName"
                  name="userName"
                  value={formData.userName}
                  onChange={handleChange}
                  placeholder="np. Jan Kowalski"
                  className="input pl-10 w-full"
                />
              </div>
            </div>

            {/* NIP */}
            <div>
              <label htmlFor="nip" className="block text-sm font-medium text-gray-700 mb-1">
                NIP firmy *
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  id="nip"
                  name="nip"
                  value={formData.nip}
                  onChange={handleNipChange}
                  placeholder="np. 123-456-78-90"
                  className={`input pl-10 w-full ${nipError ? 'border-red-400 focus:ring-red-400' : ''}`}
                  required
                />
              </div>
              {nipError ? (
                <p className="text-xs text-red-600 mt-1">{nipError}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  LexTool obsługuje wyłącznie firmy - NIP jest wymagany
                </p>
              )}
            </div>

            {/* Phone number */}
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Numer telefonu *
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="np. +48 123 456 789"
                  className="input pl-10 w-full"
                  required
                  autoComplete="tel"
                />
              </div>
            </div>

            {/* Address */}
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                Adres (opcjonalnie)
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="np. ul. Prawnicza 15, 00-001 Warszawa"
                  className="input pl-10 w-full"
                />
              </div>
            </div>

            {/* Discount code */}
            <div>
              <label htmlFor="discountCode" className="block text-sm font-medium text-gray-700 mb-1">
                Kod rabatowy (opcjonalnie)
              </label>
              <input
                type="text"
                id="discountCode"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                placeholder="Wpisz kod rabatowy"
                className="input w-full"
                maxLength={50}
              />
            </div>

            {/* Consent checkboxes */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">Zgody i oświadczenia</p>

              {/* Required: Regulamin + AUP */}
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={e => setAcceptTerms(e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  <span className="text-red-500">*</span>{' '}
                  Zapoznałem(-am) się i akceptuję{' '}
                  <a href={getLegalDoc('regulamin')?.webUrl || 'https://www.lextool.pl/regulamin'} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Regulamin LexTool
                  </a>
                  {getLegalDoc('regulamin') && (
                    <>
                      {' '}
                      <span className="text-sm text-gray-700">
                        (wersja {getLegalDoc('regulamin')!.version}, {formatLegalDate(getLegalDoc('regulamin')!.effectiveDate)})
                      </span>
                      {' '}
                      <a href={legalDocumentsApi.getPdfUrl('regulamin')} target="_blank" rel="noopener noreferrer" title="Pobierz PDF" className="inline-flex align-middle">
                        <FileDown className="w-4 h-4 text-gray-400 hover:text-blue-600" />
                      </a>
                    </>
                  )}
                  {' '}(w tym AUP).
                </span>
              </label>

              {/* Required: Polityka Prywatności */}
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptPrivacy}
                  onChange={e => setAcceptPrivacy(e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  <span className="text-red-500">*</span>{' '}
                  Potwierdzam zapoznanie się z{' '}
                  <a href={getLegalDoc('privacy_policy')?.webUrl || 'https://www.lextool.pl/polityka-prywatnosci'} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Polityką Prywatności
                  </a>
                  {getLegalDoc('privacy_policy') && (
                    <>
                      {' '}
                      <span className="text-sm text-gray-700">
                        (wersja {getLegalDoc('privacy_policy')!.version}, {formatLegalDate(getLegalDoc('privacy_policy')!.effectiveDate)})
                      </span>
                      {' '}
                      <a href={legalDocumentsApi.getPdfUrl('privacy_policy')} target="_blank" rel="noopener noreferrer" title="Pobierz PDF" className="inline-flex align-middle">
                        <FileDown className="w-4 h-4 text-gray-400 hover:text-blue-600" />
                      </a>
                    </>
                  )}
                  {' '}(informacja o przetwarzaniu danych osobowych).
                </span>
              </label>

              {/* Required: B2B + umocowanie */}
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptB2B}
                  onChange={e => setAcceptB2B(e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  <span className="text-red-500">*</span>{' '}
                  Oświadczam, że korzystam z LexTool w związku z działalnością zawodową lub gospodarczą (B2B) oraz że akceptuję Regulamin we własnym imieniu, a jeżeli działam w imieniu Organizacji – posiadam do tego należyte umocowanie.
                </span>
              </label>

              {/* Required: Dane osób trzecich + DPA */}
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptDPA}
                  onChange={e => setAcceptDPA(e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  <span className="text-red-500">*</span>{' '}
                  Jeżeli wprowadzam do LexTool dane osobowe osób trzecich, oświadczam, że mam podstawę prawną do ich przekazania, a LexTool przetwarza je jako podmiot przetwarzający zgodnie z DPA (Regulamin i Polityka Prywatności); stosuję minimalizację danych.
                </span>
              </label>

              {/* Optional: Marketing */}
              <p className="text-sm font-medium text-gray-500 mt-4">Marketing (opcjonalnie)</p>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={marketingEmail}
                  onChange={e => setMarketingEmail(e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  Wyrażam zgodę na otrzymywanie informacji handlowych drogą elektroniczną (e-mail).
                </span>
              </label>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={marketingPhone}
                  onChange={e => setMarketingPhone(e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  Wyrażam zgodę na kontakt telefoniczny/SMS w celach marketingowych.
                </span>
              </label>

              <p className="text-xs text-gray-400">
                <span className="text-red-500">*</span> Pola wymagane
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary py-3 text-base flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Tworzenie konta...
                </>
              ) : isTrial ? (
                <>
                  <Sparkles className="w-5 h-5" />
                  Utwórz konto
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  Utwórz konto i przejdź do płatności
                </>
              )}
            </button>

            {/* Disclaimer */}
            <p className="text-sm text-gray-700 text-center">
              {isTrial
                ? 'Klikając „Utwórz konto", po zaznaczeniu wymaganych pól, zawierasz umowę o świadczenie usług drogą elektroniczną i tworzysz Konto z 7-dniowym okresem próbnym zgodnie z Regulaminem.'
                : 'Klikając „Utwórz konto i przejdź do płatności", po zaznaczeniu wymaganych pól, zawierasz umowę o świadczenie usług drogą elektroniczną i tworzysz Konto zgodnie z Regulaminem.'
              }
            </p>
          </form>

          {/* Separator */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            {/* Existing user notice */}
            <p className="text-center text-sm text-gray-500">
              Masz już konto?{' '}
              <Link to="/login" className="text-blue-600 hover:underline font-medium">
                Zaloguj się
              </Link>
            </p>
          </div>

          {/* Trust indicators */}
          <div className="mt-6 flex items-center justify-center gap-6 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <Check className="w-3 h-3" />
              Bezpieczne dane
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-3 h-3" />
              Faktura VAT
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-3 h-3" />
              Anuluj kiedy chcesz
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
